import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerInputEvent, MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faCog, faSitemap, faSlash } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import { IParamsValue } from 'src/app/models/interfaces/params-value.interface';
import { ParamsValue } from 'src/app/models/params-value';
import { InheritanceInfoComponent } from '../../../controls/forms/inheritance-info/inheritance-info.component';

import { FormFocusDirective } from '../../../controls/directives/form-focus.directive';

@Component({
    selector: 'app-parameters-value-form',
    templateUrl: './parameters-value-form.component.html',
    styleUrls: ['./parameters-value-form.component.scss'],
    imports: [
        FormsModule,
        FormFocusDirective,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatDatepickerModule,
        MatCheckboxModule,
        InheritanceInfoComponent,
        MatMenuModule,
        MatIconModule,
        MatTooltipModule,
        FaIconComponent,
    ]
})
export class ParametersValueFormComponent implements OnInit {
    public faSlash = faSlash;
    public faSitemap = faSitemap;
    public faCog = faCog;
    /**
     * Infos du paramètre.
     */
    @Input()
    public paramsValue: Required<IParamsValue> | null | undefined;

    /**
     * Infos du paramètre du parent.
     */
    @Input()
    public parentValue: string;

    /**
     * Valeur indiquant si le formulaire est disabled.
     */
    private _disabled: boolean;
    @Input()
    public set disabled(value: boolean) {
        this._disabled = value;

        if (this.formGroup) {
            if (value === true) {
                this.formGroup.disable();
            } else {
                this.formGroup.enable();
            }
        }
    }

    public get disabled(): boolean {
        return this._disabled;
    }

    /**
     * Control
     */
    public formGroup: UntypedFormGroup;

    public paramDefaultValueText: string;

    /**
     * Emis lorsque le formulaire est mis à jour (clic sur le bouton sauver)
     */
    // eslint-disable-next-line @angular-eslint/no-output-rename
    @Output('paramsValueUpdated')
    public dataOutput: EventEmitter<IParamsValue> = new EventEmitter();

    ngOnInit(): void {
        if (this.paramsValue.paramType == 'Date') {
            this.formGroup = new UntypedFormGroup({
                param: new UntypedFormControl({ value: moment(this.paramsValue.value, 'DD/MM/YYYY'), disabled: this.disabled }, Validators.required),
            });
        } else if (this.paramsValue.paramType == 'Boolean') {
            this.formGroup = new UntypedFormGroup({
                param: new UntypedFormControl({ value: JSON.parse(this.paramsValue.value), disabled: this.disabled }),
            });
        } else {
            this.formGroup = new UntypedFormGroup({
                param: new UntypedFormControl({ value: this.paramsValue.value, disabled: this.disabled }, Validators.required),
            });
        }

        this.paramDefaultValueText = `Valeur par défaut : ${this.paramsValue.paramDefaultValue}`;
    }

    /**
     *
     * @param paramsValues Mise à jour des paramètres
     */
    public paramsValueUpdated(value: string): void {
        let updatedParam = ParamsValue.Map(this.paramsValue);
        updatedParam.value = value;
        updatedParam.isInherited = value == this.parentValue;
        this.dataOutput.emit(updatedParam);
    }

    public dateValueChange(event: MatDatepickerInputEvent<moment.Moment>) {
        let updatedParam = ParamsValue.Map(this.paramsValue);
        updatedParam.value = event.value.format('DD/MM/yyyy');
        updatedParam.isInherited = event.value.format('DD/MM/yyyy') == this.parentValue;
        this.dataOutput.emit(updatedParam);
    }

    public checkboxChanged(event) {
        let updatedParam = ParamsValue.Map(this.paramsValue);
        updatedParam.value = event.checked;
        updatedParam.isInherited = event.checked == this.parentValue;
        this.dataOutput.emit(updatedParam);
    }

    /**
     * Réinitialise la valeur d'un paramètre avec la valeur héritée de son parent.
     */
    public resetParentParam() {
        if (!this.disabled) {
            console.log(`Reset param from ${this.paramsValue.paramName} to ${this.parentValue} (Parent value)`);
            this.paramsValueUpdated(this.parentValue);

            if (this.paramsValue.paramType == 'Date') {
                console.log('set date');
                this.formGroup.get('param').setValue(moment(this.parentValue, 'DD/MM/YYYY'));
            } else if (this.paramsValue.paramType == 'Boolean') {
                this.formGroup.get('param').setValue(JSON.parse(this.paramsValue.value));
            } else {
                this.formGroup.get('param').setValue(this.parentValue);
            }
        }
    }

    /**
     * Réinitialise la valeur d'un paramètre avec la valeur par defaut.
     */
    public resetDefaultParam() {
        if (!this.disabled) {
            console.log(`Reset param from ${this.paramsValue.paramName} to ${this.paramsValue.paramDefaultValue} (default value)`);
            if (this.paramsValue.paramType == 'Date') {
                console.log('set date');
                this.formGroup.get('param').setValue(moment(this.paramsValue.paramDefaultValue, 'DD/MM/YYYY'));
            } else if (this.paramsValue.paramType == 'Boolean') {
                this.formGroup.get('param').setValue(JSON.parse(this.paramsValue.paramDefaultValue));
            } else {
                this.formGroup.get('param').setValue(this.paramsValue.paramDefaultValue);
            }

            this.paramsValueUpdated(this.paramsValue.paramDefaultValue);
        }
    }

    public setInheritedValue(): void {
        console.log('setInheritedValue');
        this.resetParentParam();
    }

    public setDefaultValue(): void {
        console.log('setDefaultValue');
        this.resetDefaultParam();
    }
}
